import { IProductSubfamilyDetail, pimImageToOpenGraphImage } from '@viabizzuno/models';
import { getLayout } from '@viabizzuno/models/server';
import { LayoutDefault } from '@viabizzuno/ui';
import { getPageProps } from '@viabizzuno/ui/server';
import { asEquatable, IStaticContext, PageProps } from '@websolutespa/bom-core';
import { LabelService } from '@websolutespa/bom-mixer-hooks';
import { getPage, getStaticPathsForSchema } from '@websolutespa/bom-mixer-models/server';
import { LazyLoader, OpenGraph, StructuredData } from '@websolutespa/bom-mixer-ui';
import { GetStaticPropsResult } from 'next';


export default function ProductSubfamilyDetail({ layout, page, params }: PageProps<IProductSubfamilyDetail>) {
  return (
    <>
      <LazyLoader components={page.components} />
      <StructuredData item={({ meta: { title, description, url }, brand }) => ({
        '@type': 'ItemPage' as const,
        name: title,
        description: description,
        image: page.pimJson?.foto_preview_accesa ? `https://storage.onpage.it/${page.pimJson?.foto_preview_accesa.token}/${page.pimJson?.foto_preview_accesa.name}` : undefined,
        brand: {
          '@type': 'Brand' as const,
          name: brand.name,
        },
      })} />
      <OpenGraph item={(metaData) => ({
        ...metaData.openGraph,
        image: page.pimJson?.foto_preview_accesa ? pimImageToOpenGraphImage(page.pimJson.foto_preview_accesa) : undefined,
      })} />
    </>
  );
}

ProductSubfamilyDetail.Layout = LayoutDefault;

export async function getStaticProps(context: IStaticContext): Promise<GetStaticPropsResult<PageProps<IProductSubfamilyDetail>>> {
  const id = asEquatable(context.params.id);
  const market = context.params.market;
  const locale = context.params.locale;
  const layout = await getLayout(market, locale);
  const labelService = new LabelService(layout.labels);
  const page = await getPage<IProductSubfamilyDetail>('product_subfamily_detail', id, market, locale, context);

  if (!page) {
    return {
      notFound: true,
      revalidate: true,
    };
  }

  // --- components ---
  const allCmsComponents = page.components || [];
  const cmsProjectsProposition = allCmsComponents.find(c => c.blockType === 'block_projects_proposition');
  const components: typeof allCmsComponents = [];

  // block_hero_productsubfamily (all props populated by server fn)
  components.push({
    schema: 'block_hero_productsubfamily',
  });

  // block_banner_media
  if (page.pimJson?.immagine_full) {
    components.push({
      schema: 'block_banner_media',
      media: page.pimJson.immagine_full,
    });
  }

  // block_codes
  components.push({
    schema: 'block_codes',
    title: labelService.getLabel('product_subfamily_detail.codes.title'),
  });

  // block_carousel_sibling_products
  components.push({
    schema: 'block_carousel_sibling_products',
    title: labelService.getLabel('product_subfamily_detail.carousel_products.title'),
  });

  // block_carousel_media
  if (page.pimJson?.gallery) {
    components.push({
      schema: 'block_carousel_media',
      title: labelService.getLabel('product_subfamily_detail.gallery.title'),
      items: [page.pimJson.gallery].flat().map((item: any) => ({ item })),
    });
  }

  // block_projects_proposition
  components.push(cmsProjectsProposition ?? { schema: 'block_projects_proposition' });

  // block_carousel_sametype_products
  components.push({
    schema: 'block_carousel_sametype_products',
    title: labelService.getLabel('product_subfamily_detail.sametype_products.title'),
  });


  components.push(...allCmsComponents);
  page.components = components;

  const props = await getPageProps({ ...context, layout, page });
  return {
    props,
    /*
    * Next.js will attempt to re-generate the page when a request comes in at most once every 60 seconds
    */
    revalidate: 60 * 60 * 2, // revalidate
  };
}

export async function getStaticPaths() {
  const paths = await getStaticPathsForSchema('product_subfamily_detail');
  return {
    paths,
    fallback: 'blocking', // runs before initial render
    // fallback: true, // runs in the background
  };
}

